import React, { useState, useContext, useEffect } from 'react'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'
import { useQuery } from '@tanstack/react-query'

import { UserContext } from 'context/userContext'
import { useWindowMessage } from 'hooks/useWindowMessage'
import { setToken, setRefreshToken, getProfile, useMe } from 'services/auth'
import { getBenefits } from 'services/benefits'
import { getNavigatorLocation } from 'services/geoService'

import { AcceptTermsModal } from 'components/v2/AcceptTermsModal/AcceptTermsModal'

const events = {
  onAuthenticateWebView: ({ token, refreshToken }) => {
    setToken(token)
    setRefreshToken(refreshToken)
  }
}

const FREE_ROUTES = [
  '/',
  '/termos-paciente',
  '/termos-psicologos',
  '/privacidade',
  '/virtual-room',
  '/encontre-seu-psicologo',
  '/busca',
  '/encontre-seu-psicologo/resultados',
  '/agendar-consultas/finalizar-pagamento',
  '/agendamento-realizado',
  '/entrar'
]

const UserProvider = ({ children }) => {
  const router = useRouter()
  useWindowMessage(events)

  const cookies = parseCookies()
  const [shouldAnswerVitCare, setShouldAnswerVitCare] = useState(false)

  const { data: user, refetch: refetchMe } = useMe()

  const { data: benefits, refetch: refetchBenefits } = useQuery({
    queryKey: ['benefits', user?.id],
    enabled: !!user && !isProfessional,
    queryFn: () => {
      if (!user || isProfessional) return null
      return getBenefits(user.id)
    },
    select: response => response?.data?.content ?? null
  })

  const { data: location } = useQuery({
    queryKey: ['location'],
    queryFn: () => getNavigatorLocation(),
    select: ({ coords }) => {
      return {
        lat: coords?.latitude,
        lng: coords?.longitude
      }
    }
  })

  const isUserLoggedIn = !!user
  const isUserB2B = !!user?.contract || cookies?.contractId || cookies?.['@vittude:user']?.contract
  const isProfessional = user?.role === 'professional'
  const shouldHideConsultationValue = user?.contract?.features?.hide_consultation_value
  const isPresencialConsultationEnabled = user?.contract?.features?.presential_consultation_enabled

  const hasPendingPrivacyTerm = !!user?.pending_terms?.length

  const isFreeRoute = FREE_ROUTES.includes(router?.pathname)

  return (
    <UserContext.Provider
      value={{
        user: user || getProfile(),
        benefits,
        location,
        refetchBenefits,
        isUserLoggedIn,
        isUserB2B,
        shouldAnswerVitCare,
        setShouldAnswerVitCare,
        isProfessional,
        shouldHideConsultationValue,
        isPresencialConsultationEnabled,
        refetchMe
      }}
    >
      {children}
      {hasPendingPrivacyTerm && !isFreeRoute && (
        <AcceptTermsModal pendingTerm={user.pending_terms[0]} />
      )}
    </UserContext.Provider>
  )
}

const useUser = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

export { UserProvider, useUser }
